import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Head from 'next/head';
import isEmpty from 'lodash/isEmpty';

import Container from 'components/container';
import Footer from 'components/footer';
import Header from 'components/header';
import defaultShareImage from 'images/shares/home.png';
import {
  icon48,
  icon72,
  icon96,
  icon144,
  icon168,
  icon192,
  icon256,
  icon384,
  icon512,
} from 'images/icons/iconPaths';
import { ISODateValidator } from 'utilities/proptype-validators';
import config from 'siteconfig';

const THEME_COLOR = '#0074e0';
const defaultShareImageAltText =
  'Sign held at a protest stating "demand action from your elected officials, text resist to 50409." with overlay text that says "Text RESIST to 50409"';

// Maps our content to the OG standard types
const DATA_TYPES = {
  blog: 'article',
  petition: 'resistbot:petition',
  website: 'website',
};
const SITE_URL = config.siteUrl;

const Layout = ({
  description,
  meta,
  keywords,
  title,
  image,
  imageAltText,
  dataType,
  ogTitle,
  ogDescription,
  publishedOn,
  updatedOn,
  // Could be used by any/all pages if there's possibility of misformed URLs being shared around (petitions)
  canonicalPath,
  // Some sort of special header/banner, like the homepage has
  header,
  // Elements it encloses
  children,
}) => {
  const router = useRouter();
  const seo = {
    title: title || config.title,
    description: description || config.description,
    image: new URL(image || defaultShareImage, config.siteUrl).href,
    imageAltText: imageAltText || defaultShareImageAltText,
    url: new URL(router.asPath, config.siteUrl).href,
    author: config.blog.authorTwitterUsername,
    ogTitle: ogTitle || title || config.title,
    ogDescription: ogDescription || description || config.description,
  };
  const canonical = canonicalPath
    ? new URL(canonicalPath, config.siteUrl).href
    : null;

  const _meta = [
    {
      name: `description`,
      content: seo.description,
    },
    {
      property: 'og:url',
      content: seo.url,
    },
    {
      property: `og:title`,
      content: seo.ogTitle,
    },
    {
      property: `og:description`,
      content: seo.ogDescription,
    },
    {
      property: 'og:type',
      content: dataType,
    },
    {
      property: `og:image`,
      content: seo.image,
    },
    {
      property: `og:image:alt`,
      content: seo.imageAltText,
    },
    // Twitter Specific (they use `name` rather than `property`)
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:title`,
      content: seo.ogTitle,
    },
    {
      name: `twitter:description`,
      content: seo.ogDescription,
    },
    {
      name: `twitter:image`,
      content: seo.image,
    },
    {
      name: `twitter:image:alt`,
      content: seo.imageAltText,
    },
    {
      name: `twitter:site`,
      content: seo.author,
    },
  ]
    .concat(
      keywords && keywords.length > 0
        ? {
            name: `keywords`,
            content: keywords.join(`, `),
          }
        : []
    )
    .concat(
      dataType === DATA_TYPES.blog && publishedOn
        ? {
            property: 'article:published_time',
            content: publishedOn,
          }
        : []
    )
    .concat(
      dataType === DATA_TYPES.blog && updatedOn
        ? {
            property: 'article:modified_time',
            content: updatedOn,
          }
        : []
    )
    // Theme Color sets the menu bar color on some androids and Safari
    .concat({ name: 'theme-color', content: THEME_COLOR })
    .concat(meta);

  const metaElements = _meta.map((element) => {
    if (!isEmpty(element)) {
      const keyName = element.property ? 'property' : 'name';
      const content = element.content;
      return (
        <meta
          {...{ [keyName]: element[keyName], content, key: element[keyName] }}
        />
      );
    }
  });

  return (
    <>
      <Head>
        <title>{seo.title}</title>
        <meta charSet="utf-8" />
        {metaElements.map((metaElement) => metaElement)}
        {canonical ? <link rel={'canonical'} href={canonical} /> : null}
        <link
          rel="alternate"
          type="application/rss+xml"
          href={`${SITE_URL}/feed.xml`}
        />
        <link
          rel="alternate"
          type="application/json"
          href={`${SITE_URL}/feed.json`}
        />
        <link
          rel="alternate"
          type="application/atom+xml"
          href={`${SITE_URL}/feed.atom`}
        />
        <link rel="icon" type="image/png" href={icon48} />
        <link rel="apple-touch-icon" sizes="32x32" href={icon48} />
        <link rel="apple-touch-icon" sizes="48x48" href={icon48} />
        <link rel="apple-touch-icon" sizes="72x72" href={icon72} />
        <link rel="apple-touch-icon" sizes="96x96" href={icon96} />
        <link rel="apple-touch-icon" sizes="144x144" href={icon144} />
        <link rel="apple-touch-icon" sizes="168x168" href={icon168} />
        <link rel="apple-touch-icon" sizes="192x192" href={icon192} />
        <link rel="apple-touch-icon" sizes="256x256" href={icon256} />
        <link rel="apple-touch-icon" sizes="384x384" href={icon384} />
        <link rel="apple-touch-icon" sizes="512x512" href={icon512} />
      </Head>
      <Header siteTitle={seo.title}>{header}</Header>
      <main>
        <Container>{children}</Container>
      </main>
      <Footer />
    </>
  );
};

Layout.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: null,
  title: null,
  image: null,
  imageAltText: null,
  dataType: DATA_TYPES.website,
};

Layout.propTypes = {
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  description: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  imageAltText: PropTypes.string,
  dataType: PropTypes.oneOf(Object.values(DATA_TYPES)),
  ogDescription: PropTypes.string,
  ogTitle: PropTypes.string,
  publishedOn: ISODateValidator,
  updatedOn: ISODateValidator,
  canonicalPath: PropTypes.string,
  children: PropTypes.node.isRequired,
  header: PropTypes.node,
};

export { DATA_TYPES };
export default Layout;
